import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useMe } from '../lib/me';
import { contractorAuth, contractorSignedIn } from '../lib/contractorAuth';
import { useInstallPrompt, platformHint } from '../lib/pwa';
import './InstallPage.css';

/**
 * Install Jarvis & downloads.
 *
 * Two of Gordon's requests on one page, because they are the two things a
 * person sets up once on a new machine:
 *
 *   THE DESKTOP APP. Jarvis is an installable web app - Edge and Chrome put
 *   it on the Start menu and the Dock with its own window and icon, and
 *   every deploy updates it on its own (public/sw.js, lib/pwa.js). When the
 *   browser has offered to install, there is a button; when it has not
 *   (Safari, Firefox, already installed), the OS's own steps are shown.
 *
 *   THE DEVICE AGENT. The heartbeat and diagnostics service on company
 *   machines (agent/). Since 22 Oct it installs itself: a member of staff
 *   clicks "Download Jarvis for Windows" (or Mac) and the server makes an
 *   installer with a one-shot, 48-hour token minted for them inside it -
 *   open it, approve the admin prompt, done. Nobody types a token. The IT
 *   packages (zips, for Intune and token installs) stay below, folded away;
 *   contractors get those and no installer, since their machines are not
 *   company devices. Only an administrator sees the enrolment-token form;
 *   the server refuses it to anyone else regardless.
 *
 * Works with no props and for either kind of caller: a member of staff
 * (useMe) or a contractor on a bearer token (contractorAuth).
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PLATFORM_LABEL = {
  windows: 'Windows',
  'windows-x64': 'Windows',
  'macos-arm64': 'macOS (Apple silicon)',
  'macos-intel': 'macOS (Intel)',
  macos: 'macOS',
  linux: 'Linux'
};
const platformLabel = (p) => PLATFORM_LABEL[p] || p;

const fmtBytes = (n) => {
  if (!Number.isFinite(n)) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};
const fmtDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
};

/** Copy to the clipboard, with the result shown on the button for a moment. */
function CopyButton({ text, label = 'Copy', small = false }) {
  const [state, setState] = useState('');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setState('Copied');
    } catch {
      setState('Select it and copy');
    }
    setTimeout(() => setState(''), 1800);
  };
  return (
    <button type="button" className={`btn btn-outline${small ? ' btn-small' : ''} ins-copy`} onClick={copy}
      aria-live="polite">
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <rect x="5" y="5" width="9" height="9" rx="1.5" />
        <path d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2h-6A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5" />
      </svg>
      {state || label}
    </button>
  );
}

const Icon = {
  monitor: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8M12 16v4" />
    </svg>
  ),
  download: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 4v11M7 10l5 5 5-5M4 19h16" />
    </svg>
  ),
  key: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="8" cy="14" r="4" /><path d="M11 11l9-9M16 6l3 3M13 9l3 3" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7" />
    </svg>
  ),
  refresh: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 12a8 8 0 1 1-2.3-5.7M20 4v5h-5" />
    </svg>
  ),
  windows: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3 5.5l7.5-1v7H3zM11.5 4.4L21 3v8.5h-9.5zM3 12.5h7.5v7L3 18.5zM11.5 12.5H21V21l-9.5-1.4z" />
    </svg>
  ),
  laptop: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="12" rx="2" /><path d="M2 19h20" /><path d="M10 19l.5-3h3l.5 3" />
    </svg>
  )
};

/* ------------------------------------------------------------- the app */

function InstallSteps({ hint }) {
  const { os, browser } = hint;
  if (os === 'ios') {
    return (
      <ol className="ins-steps">
        <li>Open Jarvis in <strong>Safari</strong>{browser !== 'safari' ? ' (not this browser - iPhone only installs from Safari)' : ''}.</li>
        <li>Tap the <strong>Share</strong> button (the square with an arrow).</li>
        <li>Choose <strong>Add to Home Screen</strong>, then <strong>Add</strong>.</li>
      </ol>
    );
  }
  if (os === 'android') {
    return (
      <ol className="ins-steps">
        <li>Open Jarvis in <strong>Chrome</strong> or <strong>Edge</strong>.</li>
        <li>Open the <strong>⋮</strong> menu at the top right.</li>
        <li>Choose <strong>Install app</strong> (or <strong>Add to Home screen</strong>), then <strong>Install</strong>.</li>
      </ol>
    );
  }
  if (browser === 'edge') {
    return (
      <ol className="ins-steps">
        <li>Open the <strong>⋯</strong> menu at the top right of Edge.</li>
        <li>Choose <strong>Apps</strong> → <strong>Install Jarvis</strong>. Or click the install icon that appears at the right end of the address bar.</li>
        <li>Click <strong>Install</strong>. Jarvis opens in its own window.</li>
        {/* Edge's own "App installed" panel, which a web page cannot pre-tick:
            the four boxes are Edge's, so the page can only say to tick them. */}
        <li>Edge then shows <strong>App installed</strong> with four boxes — <strong>Pin to taskbar</strong>, <strong>Pin to Start</strong>, <strong>Create desktop shortcut</strong> and <strong>Auto-start on device login</strong>. Tick all four, then <strong>Allow</strong>.</li>
      </ol>
    );
  }
  if (browser === 'chrome') {
    return (
      <ol className="ins-steps">
        <li>Open the <strong>⋮</strong> menu at the top right of Chrome.</li>
        <li>Choose <strong>Cast, save and share</strong> → <strong>Install page as app…</strong> (older versions: <strong>Install Jarvis…</strong>). Or click the install icon at the right end of the address bar.</li>
        <li>Click <strong>Install</strong>. Jarvis opens in its own window with its own icon.</li>
      </ol>
    );
  }
  if (browser === 'safari') {
    return (
      <ol className="ins-steps">
        <li>In Safari, open the <strong>File</strong> menu.</li>
        <li>Choose <strong>Add to Dock…</strong>, then <strong>Add</strong>.</li>
      </ol>
    );
  }
  return (
    <ol className="ins-steps">
      <li>This browser does not install web apps. Open Jarvis in <strong>Microsoft Edge</strong> or <strong>Google Chrome</strong>.</li>
      <li>From the browser's <strong>⋯</strong> / <strong>⋮</strong> menu, choose <strong>Install Jarvis</strong>.</li>
    </ol>
  );
}

function StartAtSignIn({ hint }) {
  const { os, browser } = hint;
  if (os === 'ios' || os === 'android') return null;
  const where = browser === 'edge'
    ? <>open <code>edge://apps</code>, click <strong>⋯</strong> on Jarvis and tick <strong>Start app when you sign in</strong></>
    : browser === 'chrome'
      ? <>open <code>chrome://apps</code>, right-click Jarvis and tick <strong>Start app when you sign in</strong></>
      : <>in Edge open <code>edge://apps</code> (in Chrome, <code>chrome://apps</code>), and on Jarvis tick <strong>Start app when you sign in</strong></>;
  return (
    <div className="ins-tip">
      <span className="ins-tip-k">Start with {os === 'mac' ? 'your Mac' : 'Windows'}</span>
      <span>Once installed, {where}. Jarvis is then open and signed in each morning.</span>
    </div>
  );
}

function DesktopAppCard() {
  const { available, installedJustNow, standalone, prompt } = useInstallPrompt();
  const hint = useMemo(() => platformHint(), []);
  const [outcome, setOutcome] = useState('');

  const install = async () => {
    const r = await prompt();
    setOutcome(r === 'accepted' ? 'Installing… look for Jarvis on your Start menu or Dock.' :
      r === 'dismissed' ? 'No problem - the button stays here for whenever you want it.' : '');
  };

  let status;
  if (standalone || installedJustNow) {
    status = (
      <div className="ins-state" data-tone="success" data-testid="install-installed">
        <span className="ins-state-ic">{Icon.check}</span>
        <div>
          <strong>{standalone ? 'You are using the installed app.' : 'Jarvis is installed.'}</strong>
          <p>{standalone ? 'Nothing more to do here.' : 'Open it from the Start menu, Dock or home screen.'}</p>
        </div>
      </div>
    );
  } else if (available) {
    status = (
      <div className="ins-state" data-testid="install-available">
        <button type="button" className="btn btn-primary btn-lg ins-install-btn" onClick={install}
          data-testid="install-button">
          {Icon.download}
          Install Jarvis
        </button>
        <p className="ins-muted">Opens the browser's install dialog. Takes a second; nothing to download.</p>
        {outcome && <p className="ins-outcome">{outcome}</p>}
      </div>
    );
  } else {
    status = (
      <div className="ins-state" data-testid="install-instructions">
        <strong className="ins-state-h">How to install it from this browser</strong>
        <InstallSteps hint={hint} />
      </div>
    );
  }

  return (
    <section className="ins-card ins-hero" aria-labelledby="ins-app-h">
      <div className="ins-hero-mark" aria-hidden="true">
        <img src="/icons/icon-192.png" alt="" width="72" height="72" />
      </div>
      <div className="ins-hero-body">
        <h2 id="ins-app-h">Jarvis on your desktop</h2>
        <p className="ins-lead">
          Install Jarvis as an app: its own window, its own icon on the Start menu or Dock, no browser
          tabs to hunt through. It is the same Jarvis as the website, so your sign-in and settings carry over.
        </p>
        {status}
        <StartAtSignIn hint={hint} />
        <div className="ins-tip" data-testid="install-updates-note">
          <span className="ins-tip-k">{Icon.refresh} Always current</span>
          <span>
            Every deploy updates the installed app automatically. When a new version is live you will see
            a small <em>Jarvis has been updated</em> bar - click <strong>Reload</strong> and you are on it.
            There is nothing to reinstall, ever.
          </span>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------- the agent */

/** What the server said, from a JSON body or - for a file request - a Blob. */
async function errorText(e, fallback) {
  const data = e?.response?.data;
  if (data && typeof data === 'object' && typeof data.error === 'string') return data.error;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (parsed?.error) return parsed.error;
    } catch { /* not JSON */ }
  }
  return fallback;
}

/** Hand bytes to the browser as a download under a name. */
function saveBlob(blob, name) {
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10000);
}

const fmtUntil = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }) : '';
};

/**
 * The one-click installers (Gordon, 22 Oct: "installs automatically once
 * it's downloaded instead of the users having to write the script").
 *
 * Each click asks the server for a fresh installer with a token minted for
 * this person inside it (POST /downloads/agent/setup), so the file works
 * once, within 48 hours, and enrols the machine as theirs. Staff only: the
 * server refuses contractors, and this card does not offer them the button.
 */
function SetupCards({ latest, hint, onError }) {
  const [busy, setBusy] = useState('');
  const [done, setDone] = useState(null);
  const setup = latest?.setup || [];
  const win = setup.find((s) => s.platform === 'windows-x64');
  const macs = setup.filter((s) => /^macos/.test(s.platform));
  const onMac = hint.os === 'mac';

  const get = async (s) => {
    setBusy(s.platform);
    setDone(null);
    onError('');
    try {
      const r = await axios.post(`${API_BASE}/downloads/agent/setup`, { platform: s.platform },
        { responseType: 'blob', ...contractorAuth() });
      saveBlob(r.data, s.file_name);
      setDone({ platform: s.platform, file_name: s.file_name, until: r.headers?.['x-setup-expires'] || null });
    } catch (e) {
      onError(await errorText(e, 'The installer could not be made. Try again in a moment.'));
    } finally {
      setBusy('');
    }
  };

  const doneFor = (p) => done && done.platform.startsWith(p) && (
    <p className="ins-outcome" data-testid="setup-done" role="status">
      {Icon.check}
      <span>
        <strong>{done.file_name}</strong> is in your downloads. It works once
        {done.until ? <>, until {fmtUntil(done.until)}</> : ', within 48 hours'}.
      </span>
    </p>
  );

  const windowsCard = win && (
    <div className="ins-setup" data-primary={String(!onMac)} data-testid="setup-card-windows" key="win">
      <div className="ins-setup-head">
        <span className="ins-setup-os">{Icon.windows}</span>
        <h3>Windows</h3>
        {hint.os === 'windows' && <span className="ins-chip">This computer</span>}
      </div>
      <button type="button" className="btn btn-primary btn-lg ins-setup-btn" disabled={busy === win.platform}
        onClick={() => get(win)} data-testid="setup-windows">
        {Icon.download}
        {busy === win.platform ? 'Preparing your installer…' : 'Download Jarvis for Windows'}
      </button>
      <p className="ins-setup-meta">{win.file_name} · {fmtBytes(win.size_bytes)} · made for you, works once</p>
      {doneFor('windows')}
      <ol className="ins-steps-num">
        <li><span>Open <strong>Jarvis-Setup.exe</strong> from your downloads.</span></li>
        <li><span>Choose <strong>Yes</strong> when Windows asks to allow it to make changes.</span></li>
        <li><span>Done. A message says it is installed, and this computer appears in Jarvis within a few minutes.</span></li>
      </ol>
      <div className="ins-tip ins-tip-tight" data-testid="smartscreen-note">
        <span className="ins-tip-k">If Windows warns you</span>
        <span>
          Windows may say it protected your PC. Choose <strong>More info</strong> → <strong>Run anyway</strong>.
          This goes away once the installer is code-signed.
        </span>
      </div>
    </div>
  );

  const macCard = macs.length > 0 && (
    <div className="ins-setup" data-primary={String(onMac)} data-testid="setup-card-mac" key="mac">
      <div className="ins-setup-head">
        <span className="ins-setup-os">{Icon.laptop}</span>
        <h3>Mac</h3>
        {onMac && <span className="ins-chip">This computer</span>}
      </div>
      <div className="ins-setup-pair">
        {macs.map((s) => (
          <button key={s.platform} type="button" className={`btn ${onMac ? 'btn-primary' : 'btn-secondary'} ins-setup-btn`}
            disabled={busy === s.platform} onClick={() => get(s)} data-testid={`setup-${s.platform}`}>
            {Icon.download}
            {busy === s.platform ? 'Preparing…' : `Download for ${s.platform === 'macos-arm64' ? 'Apple silicon' : 'Intel'}`}
          </button>
        ))}
      </div>
      <p className="ins-setup-meta">
        Not sure which? Apple menu → <strong>About This Mac</strong>: a chip called “Apple M…” is Apple silicon.
      </p>
      {doneFor('macos')}
      <ol className="ins-steps-num">
        <li><span>Open the download to unzip it.</span></li>
        <li><span>Right-click <strong>Install Jarvis.command</strong> and choose <strong>Open</strong> (and <strong>Open</strong> again when macOS asks - it is not signed by Apple yet).</span></li>
        <li><span>Enter your Mac password. Done: this Mac appears in Jarvis within a few minutes.</span></li>
      </ol>
    </div>
  );

  if (!windowsCard && !macCard) return null;
  return (
    <div className="ins-setup-grid" data-testid="setup-cards">
      {onMac ? [macCard, windowsCard] : [windowsCard, macCard]}
    </div>
  );
}

function AgentDownloads({ me }) {
  const [latest, setLatest] = useState(undefined); // undefined loading, null none
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [done, setDone] = useState('');
  const hint = useMemo(() => platformHint(), []);
  const staff = Boolean(me?.signedIn);

  useEffect(() => {
    let live = true;
    axios.get(`${API_BASE}/downloads/agent/latest`, contractorAuth())
      .then((r) => { if (live) setLatest(r.data); })
      .catch((e) => {
        if (!live) return;
        if (e.response?.status === 404) setLatest(null);
        else setError(e.response?.data?.error || 'Could not load the downloads.');
      });
    return () => { live = false; };
  }, []);

  /*
   * The file needs the caller's credential, which a plain link does not
   * carry - the staff token lives in an Authorization header. So it is
   * fetched through axios like every other call and handed to the browser
   * as a download.
   */
  const download = async (f) => {
    setBusy(f.file_name);
    setDone('');
    try {
      const r = await axios.get(f.url.startsWith('http') ? f.url : `${API_BASE}${f.url.replace(/^\/api/, '')}`,
        { responseType: 'blob', ...contractorAuth() });
      saveBlob(r.data, f.file_name);
      setDone(f.file_name);
    } catch (e) {
      setError(await errorText(e, 'The download did not start. Try again in a moment.'));
    } finally {
      setBusy('');
    }
  };

  const api = API_BASE;
  const files = latest?.files || [];
  const hasSetup = (latest?.setup || []).length > 0;
  // The IT packages are folded away behind the one-click installers, and
  // open on their own when there is nothing else to offer.
  const itOpen = !hasSetup || !staff;

  return (
    <section className="ins-card" aria-labelledby="ins-agent-h" data-testid="agent-downloads">
      <div className="ins-card-head">
        <span className="ins-card-ic">{Icon.monitor}</span>
        <div>
          <h2 id="ins-agent-h">The Jarvis agent for company computers</h2>
          <p className="ins-lead">
            A small background service for company laptops and desktops. Once a day it tells Jarvis the
            machine's serial, operating system, disk encryption and free space, so the asset register is
            right without anyone typing; and when IT asks, it runs a read-only diagnostic on that one
            machine. It never reads files, email or the screen, and it never changes anything.
          </p>
        </div>
      </div>

      {error && <div className="ins-note" data-tone="danger" role="alert">{error}</div>}

      {latest === undefined && !error && <p className="ins-muted">Looking for the latest version…</p>}

      {latest === null && (
        <div className="ins-note" data-testid="agent-none">
          No agent build has been published yet. {me?.isAdmin
            ? <>Package one with <code>npm run agent:package</code> in <code>backend/</code> and deploy - see <code>backend/agent-dist/README.md</code>.</>
            : 'Ask IT.'}
        </div>
      )}

      {latest && (
        <>
          <div className="ins-release">
            <span className="ins-chip">Version {latest.version}</span>
            {latest.released_at && <span className="ins-muted">Released {fmtDate(latest.released_at)}</span>}
          </div>

          {staff && hasSetup && <SetupCards latest={latest} hint={hint} onError={setError} />}
          {!staff && hasSetup && (
            <div className="ins-note" data-testid="setup-staff-only">
              The one-click installer is for NCMB company computers, so it is offered to NCMB staff only.
              Your own machine does not need the agent.
            </div>
          )}
          {staff && hasSetup && (
            <p className="ins-muted ins-setup-foot">
              Intune installs the agent on most company machines by itself; this is for one it has not reached yet.
              Each download is made for you and joins the computer to Jarvis as yours.
            </p>
          )}

          <details className="ins-it" open={itOpen} data-testid="it-packages">
            <summary>
              <span className="ins-it-k">For IT</span>
              Packages for Intune and installs with an enrolment token
            </summary>
            <div className="ins-dl-grid">
              {files.map((f) => (
                <div className="ins-dl" key={f.file_name}>
                  <button type="button" className="btn btn-secondary" disabled={busy === f.file_name}
                    onClick={() => download(f)} data-testid={`download-${f.platform}`}>
                    {Icon.download}
                    {busy === f.file_name ? 'Preparing…' : `${platformLabel(f.platform)} package (zip)`}
                  </button>
                  <div className="ins-dl-meta">
                    <span>{f.file_name}</span>
                    <span>·</span>
                    <span>{fmtBytes(f.size_bytes)}</span>
                  </div>
                  <div className="ins-sha">
                    <span className="ins-sha-k">SHA-256</span>
                    <code>{f.sha256}</code>
                    <CopyButton text={f.sha256} small />
                  </div>
                </div>
              ))}
            </div>
            {done && <p className="ins-outcome" data-testid="download-done">Downloaded {done}.</p>}
            <ol className="ins-steps ins-steps-wide">
              <li>Unzip the package. Inside is the agent{files.some((f) => /^windows/.test(f.platform)) ? ', a README and install.cmd' : ' and a README'}.</li>
              <li>
                As administrator (Intune: as System), run
                <span className="ins-cmd">
                  <code>{`jarvis-agent.exe install --server ${api} --token <enrolment token>`}</code>
                  <CopyButton text={`jarvis-agent.exe install --server ${api} --token `} small />
                </span>
                with an enrolment token{me?.isAdmin ? ' (make one below)' : ' from IT'}. On a Mac, the same with <code>sudo</code>.
              </li>
              <li>The service starts at once, and the machine appears in the register within a few minutes.</li>
            </ol>
          </details>
        </>
      )}
    </section>
  );
}

/* -------------------------------------------------- the enrolment token */

function EnrolmentToken() {
  const [label, setLabel] = useState('Installed by hand');
  const [days, setDays] = useState(7);
  const [fresh, setFresh] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const api = API_BASE;

  const make = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await axios.post(`${API_BASE}/asset-admin/enrolment-tokens`, { label, days: Number(days) });
      setFresh(r.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not make the token.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ins-card" aria-labelledby="ins-token-h" data-testid="enrolment-token">
      <div className="ins-card-head">
        <span className="ins-card-ic">{Icon.key}</span>
        <div>
          <h2 id="ins-token-h">Enrolment token <span className="ins-chip ins-chip-admin">Administrators</span></h2>
          <p className="ins-lead">
            For Intune and installs from the IT package. The one-click installers above carry their own
            one-shot token, so people do not need one of these. A token joins machines to the register;
            each machine then gets a key of its own. The token is shown once and Jarvis keeps only a hash
            of it. Manage or revoke tokens, and approve machines waiting for you, under{' '}
            <Link to="/settings/assets/enrolment">Settings → Assets &amp; devices → Enrolment</Link>.
          </p>
        </div>
      </div>

      {!fresh && (
        <form className="ins-token-form" onSubmit={make}>
          <div className="form-group">
            <label htmlFor="ins-tok-label">What it is for</label>
            <input id="ins-tok-label" value={label} onChange={(e) => setLabel(e.target.value)} required />
          </div>
          <div className="form-group ins-token-days">
            <label htmlFor="ins-tok-days">Lasts (days)</label>
            <input id="ins-tok-days" type="number" min={1} max={365} value={days}
              onChange={(e) => setDays(e.target.value)} required />
          </div>
          <button type="submit" className="btn btn-primary" disabled={busy || !label.trim()} data-testid="make-token">
            {busy ? 'Making…' : 'Make a token'}
          </button>
        </form>
      )}
      {error && <div className="ins-note" data-tone="danger" role="alert">{error}</div>}

      {fresh && (
        <div className="ins-note" data-tone="warning" data-testid="fresh-token">
          <p className="ins-note-h">Copy this now - Jarvis cannot show it again.</p>
          <div className="ins-cmd">
            <code data-testid="token-value">{fresh.token}</code>
            <CopyButton text={fresh.token} label="Copy token" />
          </div>
          <p className="ins-muted">Install command, run as administrator on the machine:</p>
          <div className="ins-cmd">
            <code>{`jarvis-agent.exe install --server ${api} --token ${fresh.token}`}</code>
            <CopyButton text={`jarvis-agent.exe install --server ${api} --token ${fresh.token}`} label="Copy command" />
          </div>
          <p className="ins-muted">
            Lasts until {fmtDate(fresh.expires_at)}.{' '}
            <button type="button" className="btn btn-plain btn-small" onClick={() => setFresh(null)}>Make another</button>
          </p>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------- the page */

export default function InstallPage() {
  const me = useMe();
  const isAdmin = Boolean(me?.signedIn && me?.isAdmin);
  const signedIn = Boolean(me?.signedIn) || contractorSignedIn();

  return (
    <div className="container ins-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Install Jarvis &amp; downloads</h1>
          <p className="page-description">
            Jarvis as an app on this computer or phone, and the agent IT installs on company machines.
          </p>
        </div>
      </div>

      <DesktopAppCard />
      {signedIn && <AgentDownloads me={me} />}
      {isAdmin && <EnrolmentToken />}
    </div>
  );
}
